import { z } from 'zod'

import { apiScoping } from '@/lib/api/scoping'

export type PutDocumentSourceInput = {
  plantId: string
  documentId: string
  contentBase64: string
  /** Aborted by the provider's sync bound so a hung upload can never
   * land hours later and overwrite a newer source with stale content. */
  signal?: AbortSignal
}

/**
 * Create-or-replace the editable ARA source blob (structuredAra + section
 * HTML) for a kind=ara draft. Writes are status-gated server-side: drafts
 * only, so a live document can't be edited under its audience's feet.
 */
export async function putDocumentSource(input: PutDocumentSourceInput): Promise<void> {
  await apiScoping.put(
    `/api/v1/plant-assessment/${input.plantId}/docs/${input.documentId}/source`,
    { contentBase64: input.contentBase64 },
    { signal: input.signal }
  )
}

export const DocumentSourceResponseSchema = z.looseObject({
  contentBase64: z.string()
})

export type DocumentSourceResponse = z.infer<typeof DocumentSourceResponseSchema>

/**
 * Downloads the editable ARA source for rehydration (any browser, any
 * device). Allowed for drafts (editing) and published ARAs (read-only view).
 */
export async function getDocumentSource(input: {
  plantId: string
  documentId: string
}): Promise<DocumentSourceResponse> {
  const res = await apiScoping.get(
    `/api/v1/plant-assessment/${input.plantId}/docs/${input.documentId}/source`
  )
  const result = DocumentSourceResponseSchema.safeParse(res.data)
  if (!result.success) {
    console.error(
      '[documents-source] Zod parse failed - raw response:',
      res.data,
      'issues:',
      result.error.issues
    )
    throw result.error
  }
  return result.data
}
